import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AttachmentButtons } from 'components/AttachmentButtons/AttachmentButtons';
import AttachmentInput from 'components/AttachmentInput/AttachmentInput';
import AttachmentVideoInput from 'components/AttachmentVideoInput/AttachmentVideoInput';
import {
  ACCEPTED_FILE_TYPES,
  ACCEPTED_IMAGE_TYPES,
  MAX_FILE_SIZE_MB,
  MAX_FILES_ALLOWED,
  MAX_IMAGE_SIZE_MB,
  MAX_IMAGES_ALLOWED,
} from 'utils/constants';
import clsx from 'clsx';
import ImagePreview from 'components/AttachmentPreview/ImagePreview';
import useAttachment from 'hooks/useAttachment';
import { PostAttachmentType } from '__generated__/graphql/legacy/graphql';
import FilePreview from 'components/AttachmentPreview/FilePreview';
import AltTextModal from 'components/AltTextModal/AltTextModal';
import { Attachment } from 'schema/Post/types';
import { useTranslation } from 'react-i18next';
import { useCommunityContext } from 'components/CommunityContext';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import styles from './AttachmentManager.module.scss';

interface AttachmentManagerProps {
  initValue: Attachment[];
  isCommentForm: boolean;
  multiple?: boolean;
  onAttachmentChange: (attachments: Attachment[]) => void;
  onUploadingStateChange: (isUploading: boolean) => void;
  portalElement?: HTMLElement | null;
}

const AttachmentManager = ({
  initValue = [],
  isCommentForm,
  multiple = false,
  onAttachmentChange,
  onUploadingStateChange,
  portalElement,
}: AttachmentManagerProps) => {
  const { t } = useTranslation();
  const { community, trackEvent } = useCommunityContext();

  const {
    isUploading,
    uploadFiles,
    removeAttachment,
    attachments,
  } = useAttachment(initValue);
  const [
    attachmentType,
    setAttachmentType,
  ] = useState<PostAttachmentType | null>(
    initValue.length > 0 ? initValue[0].attachmentType : null
  );
  const [fileError, setFileError] = useState<string>('');
  const [showAltTextModal, setShowAltTextModal] = useState(false);

  const altTextImageIndex = useRef<number | null>(null);

  useEffect(() => {
    setFileError('');
  }, [attachmentType]);

  useEffect(() => {
    onAttachmentChange(attachments);
  }, [attachments]);

  useEffect(() => {
    onUploadingStateChange(isUploading);
  }, [isUploading]);

  const onAttachmentBtnClick = (type: PostAttachmentType) => {
    trackEvent(TrackingEvent.ATTACHMENT_CLICKED, {
      [TrackingProperty.ATTACHMENT]: type,
    });
    setAttachmentType(type);
  };

  const closeAttachmentInput = () => {
    setAttachmentType(null);
  };

  const onFileSelected = async (files: File[]) => {
    if (!attachmentType) return;
    setFileError('');
    await uploadFiles(files, attachmentType);
  };

  const onAltTextChange = (altText: string) => {
    if (altTextImageIndex.current !== null) {
      attachments[altTextImageIndex.current].altText = altText;
      onAttachmentChange(attachments);
      if (altText) {
        trackEvent(TrackingEvent.ALT_TEXT_ADDED);
      }
    }
  };

  const onFileSelectedError = (errorMessage: string) => {
    setFileError(errorMessage);
  };

  const onFileRemoved = (index: number) => {
    removeAttachment(index);
  };

  const renderAttachmentInput = () => {
    const attachmentInputs = {
      image: (
        <AttachmentInput
          accept={ACCEPTED_IMAGE_TYPES}
          ariaControls=""
          attachmentCount={attachments.length}
          buttonLabel={multiple ? t('Add images') : t('Add image')}
          errorMessage={fileError}
          isUploading={isUploading}
          maxFilesAllowed={multiple ? MAX_IMAGES_ALLOWED : 1}
          maxFileSizeInMb={MAX_IMAGE_SIZE_MB}
          multiple={multiple}
          onClose={closeAttachmentInput}
          onFileSelected={onFileSelected}
          onFileSelectedError={onFileSelectedError}
        />
      ),
      video: (
        <AttachmentVideoInput
          attachmentUrl={attachments[0]?.url}
          buttonLabel={t('Add video')}
          cancelButtonLabel={t('Cancel')}
          inputLabel={t('Video URL')}
          onChange={(attachment: Attachment) => {
            if (attachment) {
              onAttachmentChange([attachment]);
            } else {
              // TODO: Remove this after redesign, temporary handling to remove the video attachment,
              // video component sends on change with undefined when removing the video attachment
              onFileRemoved(0);
            }
          }}
          onClose={closeAttachmentInput}
        />
      ),
      file: (
        <AttachmentInput
          accept={ACCEPTED_FILE_TYPES}
          ariaControls=""
          attachmentCount={attachments.length}
          buttonLabel={multiple ? t('Add files') : t('Add file')}
          errorMessage={fileError}
          isUploading={isUploading}
          maxFilesAllowed={multiple ? MAX_FILES_ALLOWED : 1}
          maxFileSizeInMb={MAX_FILE_SIZE_MB}
          multiple={multiple}
          onClose={closeAttachmentInput}
          onFileSelected={onFileSelected}
          onFileSelectedError={onFileSelectedError}
        />
      ),
    };

    return attachmentType ? attachmentInputs[attachmentType] : null;
  };

  const renderAttachmentPreviews = () => {
    if (attachments.length === 0) return null;

    const attachmentPreviews = {
      image: (
        <ImagePreview
          attachments={attachments}
          openAltTextModal={(index: number) => {
            altTextImageIndex.current = index;
            setShowAltTextModal(true);
          }}
          t={t}
          onRemove={onFileRemoved}
        />
      ),
      file: (
        <FilePreview attachments={attachments} t={t} onRemove={onFileRemoved} />
      ),
    };

    return attachmentType ? attachmentPreviews[attachmentType] : null;
  };

  const renderAttachmentBox = () => (
    <div
      className={clsx(styles['attachment-box'], {
        [styles['has-error']]: fileError,
      })}
    >
      {renderAttachmentPreviews()}
      {renderAttachmentInput()}
    </div>
  );

  return (
    <>
      {attachmentType && portalElement
        ? createPortal(renderAttachmentBox(), portalElement)
        : attachmentType && renderAttachmentBox()}
      <AttachmentButtons
        isCommentForm={isCommentForm}
        isDisabled={!!attachmentType}
        onBtnClick={onAttachmentBtnClick}
      />
      {showAltTextModal && (
        <AltTextModal
          attachment={
            altTextImageIndex.current !== null
              ? attachments[altTextImageIndex.current]
              : null
          }
          primaryColor={community.primaryColor}
          submitAltText={(altText: string) => {
            onAltTextChange(altText);
            setShowAltTextModal(false);
          }}
          t={t}
          onClose={() => setShowAltTextModal(false)}
        />
      )}
    </>
  );
};

export default AttachmentManager;
